import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { UserFull } from '../types/user';

interface UserContextType {
  currentUser: UserFull | null;
  loading: boolean;
  error: string | null;
  refreshCurrentUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isTokenValid, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<UserFull | null>(null);
  const [loading, setLoading] = useState(true); // Começar com loading = true
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);

  const fetchCurrentUser = useCallback(async () => {
    if (!isTokenValid()) return;

    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        throw new Error('Token não encontrado');
      }

      // Buscar informações do usuário atual
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/usuarios/eu`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      } else {
        // Se o status indicar problema de autenticação/autorização, limpar sessão
        if (response.status === 401 || response.status === 403) {
          console.warn(`Token inválido detectado (${response.status}). Limpando sessão e redirecionando para login.`);
          logout(); // Limpar token e dados de autenticação
          setCurrentUser(null); // Limpar dados do usuário
          navigate('/login', { replace: true }); // Redirecionar para login
          return; // Não continuar com o fluxo normal
        }
        throw new Error(`Falha ao buscar informações do usuário: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      const error = err as any;

      // Verificar se é erro de autenticação baseado no status HTTP
      if (error?.statusCode === 401 || error?.statusCode === 403 || error?.status === 401 || error?.status === 403) {
        console.warn(`Erro de autenticação detectado (${error?.statusCode || error?.status}). Limpando sessão e redirecionando para login.`);
        logout();
        setCurrentUser(null);
        navigate('/login', { replace: true });
        return;
      }

      setError(error instanceof Error ? error.message : 'Erro desconhecido');
      console.error('Erro ao buscar usuário atual:', err);
    } finally {
      setLoading(false);
    }
  }, [isTokenValid, logout, navigate, getToken]);

  const refreshCurrentUser = () => {
    fetchCurrentUser();
  };

  // Inicialização e listener de storage combinados
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Como usamos secureStorage com prefixo 'app_secure_data', monitorar essa chave
      if (e.key === 'app_secure_data') {
        const token = getToken();
        if (token && isTokenValid()) {
          // Token foi adicionado/modificado, buscar usuário
          fetchCurrentUser();
        } else if (!token) {
          // Token foi removado, limpar usuário
          setCurrentUser(null);
          initializedRef.current = false;
        }
      }
    };

    const handleUserLoggedIn = () => {
      // Evento customizado disparado após login bem-sucedido
      if (isTokenValid()) {
        fetchCurrentUser();
      }
    };

    // Inicialização apenas uma vez
    if (!initializedRef.current) {
      const token = getToken();
      if (token && isTokenValid()) {
        fetchCurrentUser();
      } else {
        setCurrentUser(null);
        setLoading(false); // Não há token válido, parar loading
      }
      initializedRef.current = true;
    }

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
    };
  }, [fetchCurrentUser, isTokenValid, getToken]); // Adicionadas dependências para satisfazer ESLint

  // Verificação adicional durante navegação
  useEffect(() => {
    // Só verificar em rotas protegidas
    const isProtectedRoute = !['/login', '/forgot-password', '/reset-password', '/set-password'].includes(location.pathname);
    
    if (isProtectedRoute && !isTokenValid()) {
      console.warn('Token inválido detectado durante navegação. Redirecionando para login.');
      logout();
      navigate('/login', { replace: true });
    }
  }, [location.pathname, isTokenValid, logout, navigate]);

  return (
    <UserContext.Provider value={{ currentUser, loading, error, refreshCurrentUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a UserProvider');
  }
  return context;
};